import { type FormEvent, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../api/client';
import type { Organization } from '@dfx-transfer/shared-types';
import { RoleGuard } from '../components/RoleGuard';

export function OrgsPage() {
  return (
    <RoleGuard allow={['dfx_super_admin']}>
      <OrgsPageInner />
    </RoleGuard>
  );
}

function OrgsPageInner() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [error, setError] = useState<string | null>(null);

  const orgs = useQuery({
    queryKey: ['orgs'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return apiRequest<Organization[]>('/api/v1/orgs', { token });
    },
  });

  const createOrg = useMutation({
    mutationFn: async (input: { name: string; slug: string }) => {
      const token = await getAccessTokenSilently();
      return apiRequest<Organization>('/api/v1/orgs', { token, method: 'POST', body: input });
    },
    onSuccess: () => {
      setName('');
      setSlug('');
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['orgs'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!name || !slug) return;
    createOrg.mutate({ name, slug });
  }

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-semibold">Organizations</h1>
        <p className="mt-1 text-[color:var(--color-text-secondary)]">
          Licensors and their users. Each org is fully isolated from the others.
        </p>
      </header>

      <section className="rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-card)] p-6">
        <h2 className="mb-4 text-lg font-medium">Create organization</h2>
        <form onSubmit={onSubmit} className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="text-sm text-[color:var(--color-text-secondary)]">Name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Studios"
              className="mt-1 w-full rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-primary)] px-3 py-2"
              required
            />
          </label>
          <label className="block">
            <span className="text-sm text-[color:var(--color-text-secondary)]">Slug</span>
            <input
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              placeholder="acme-studios"
              className="mt-1 w-full rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-primary)] px-3 py-2 font-mono text-sm"
              pattern="[a-z0-9](?:[a-z0-9-]*[a-z0-9])?"
              required
            />
          </label>
          {error && (
            <div className="sm:col-span-2 rounded border border-[color:var(--color-error-muted)] p-3 text-sm text-[color:var(--color-error)]">
              {error}
            </div>
          )}
          <div className="sm:col-span-2">
            <button
              type="submit"
              disabled={createOrg.isPending}
              className="rounded bg-[color:var(--color-crimson)] px-5 py-2 font-medium text-white disabled:opacity-50"
            >
              {createOrg.isPending ? 'Creating…' : 'Create organization'}
            </button>
          </div>
        </form>
      </section>

      <section>
        <h2 className="mb-4 text-lg font-medium">All organizations</h2>
        {orgs.isLoading ? (
          <div>Loading…</div>
        ) : orgs.data?.length ? (
          <ul className="divide-y divide-[color:var(--color-border)] rounded border border-[color:var(--color-border)]">
            {orgs.data.map((o) => (
              <li key={o.id} className="flex items-center justify-between px-4 py-3">
                <div>
                  <div className="font-medium">{o.name}</div>
                  <div className="font-mono text-xs text-[color:var(--color-text-muted)]">{o.slug}</div>
                </div>
                <div className="font-mono text-xs text-[color:var(--color-text-muted)]">
                  quota {(o.storage_quota_bytes / 1_000_000_000).toFixed(0)} GB
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-[color:var(--color-text-secondary)]">No organizations yet.</div>
        )}
      </section>
    </div>
  );
}
